import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { formatString } from '../../utils/StringUtils';

const MAX_CHARACTERS = 500;

const LoteModal = ({ isOpen, dfd, lote, onClose, onSuccess }) => {
  const [descricao, setDescricao] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setDescricao(lote?.descricao || '');
      setIsSubmitting(false);
      setError(null);
    }
  }, [isOpen, lote]);

  const charCount = descricao.length;

  const validateForm = useCallback(() => {
    let dfdNumValue = dfd?.num || lote?.dfdNum || '';
    if (!descricao.trim()) {
      setError('Por favor, informe a descrição do lote');
      return false;
    }
    if (descricao.length > MAX_CHARACTERS) {
      setError(`A descrição não pode exceder ${MAX_CHARACTERS} caracteres`);
      return false;
    }
    if (!dfdNumValue || String(dfdNumValue).trim() === '') {
      setError('DFD não informado. Não é possível salvar o lote.');
      return false;
    }
    setError(null);
    return true;
  }, [descricao, dfd, lote]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      let dfdNumValue, cnpjValue, idValue;
      if (lote) {
        dfdNumValue = lote.dfdNum || dfd?.num || '';
        cnpjValue = lote.cnpj || dfd?.cnpj || '';
        idValue = lote.id;
      } else {
        dfdNumValue = dfd?.num || '';
        cnpjValue = dfd?.cnpj || '';
        idValue = undefined;
      }
      dfdNumValue =
        dfdNumValue !== undefined && dfdNumValue !== null && dfdNumValue !== ''
          ? Number(dfdNumValue)
          : undefined;
      cnpjValue = cnpjValue ? String(cnpjValue) : '';
      const payload = {
        cnpj: cnpjValue,
        dfdNum: dfdNumValue,
        descricao,
        id: idValue,
        modo: lote ? 'editar' : 'criar',
      };
      await onSuccess(payload);
      onClose();
    } catch (err) {
      setError(
        lote
          ? 'Ocorreu um erro ao editar o lote. Por favor, tente novamente.'
          : 'Ocorreu um erro ao cadastrar o lote. Por favor, tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, onSuccess, dfd, descricao, lote, onClose]);

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      backdrop="static"
      keyboard={false}
      // size="lg"
    >
      <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
        <h6 className="modal-title text-center m-0">
          <i className="bi bi-boxes me-2"></i>
          {lote ? (
            <>Editar Lote do DFD Nº {formatString(dfd?.num, 4)}</>
          ) : (
            <>Cadastrar Lote do DFD Nº {formatString(dfd?.num, 4)}</>
          )}
        </h6>
        <button
          className="btn btn-link text-white p-0 mb-0"
          onClick={onClose}
          title="Fechar"
          disabled={isSubmitting}
          style={{ fontSize: '0.9rem', textDecoration: 'none' }}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        <Form.Group controlId="formDescricaoLote">
          <Form.Label>
            Descrição do Lote ({charCount}/{MAX_CHARACTERS} caracteres)
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={descricao}
            onChange={e => setDescricao(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Descreva detalhadamente o lote..."
            disabled={isSubmitting}
            className={error ? 'is-invalid' : ''}
            maxLength={MAX_CHARACTERS}
            autoFocus
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer className="py-1">
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}
          className="btn-sm py-1 px-3"
        >
          <i className="bi bi-x-lg me-1 fs-6"></i>
          Cancelar
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={isSubmitting || !descricao.trim()}
          className="btn-sm py-1 px-3"
        >
          {isSubmitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              Processando...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1 fs-6"></i>
              {lote ? 'Salvar Alterações' : 'Salvar Lote'}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

LoteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  dfd: PropTypes.shape({
    num: PropTypes.string,
    cnpj: PropTypes.string,
  }).isRequired,
  lote: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    descricao: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default LoteModal;
